import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, Lock, Eye, Key,
  Copy, Check, ChevronDown, ChevronUp, ExternalLink,
  CheckCircle2, Info, Zap, AlertCircle, ClipboardCopy
} from "lucide-react";
import { SocialIcon } from "@/components/social-icon";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

const SECURITY_POINTS = [
  {
    icon: Lock,
    title: "Your password never touches us",
    desc: "We use OAuth 2.0 — you log in directly on the platform's own website. We only receive a limited-access token, never your credentials.",
  },
  {
    icon: Shield,
    title: "Minimal permissions, always",
    desc: "We request only what we need to post on your behalf. We cannot read your messages, follow/unfollow people, or access your payment info.",
  },
  {
    icon: Eye,
    title: "You can revoke access anytime",
    desc: "Disconnect from this page or go directly to the platform's security settings. One click removes all access instantly.",
  },
  {
    icon: Key,
    title: "Prefer to stay in control?",
    desc: "Skip the connection entirely — just copy your generated post and paste it yourself. It works just as well and nothing is ever shared.",
  },
];

const PLATFORMS = [
  {
    id: "twitter" as const,
    name: "Twitter / X",
    bg: "bg-sky-500/10",
    border: "border-sky-500/20",
    glow: "shadow-sky-500/10",
    desc: "Post tweets and threads in your voice",
    permissions: ["Read your public profile", "Post on your behalf"],
    copyInstructions: "Go to twitter.com → click 'Post' → paste your generated tweet → click Post.",
    comingSoon: true,
  },
  {
    id: "linkedin" as const,
    name: "LinkedIn",
    bg: "bg-blue-600/10",
    border: "border-blue-600/20",
    glow: "shadow-blue-500/10",
    desc: "Publish posts and articles to your LinkedIn profile",
    permissions: ["Read your public profile", "Create posts on your behalf"],
    copyInstructions: "Go to linkedin.com → click 'Start a post' → paste your generated post → click Post.",
    comingSoon: true,
  },
  {
    id: "reddit" as const,
    name: "Reddit",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    glow: "shadow-orange-500/10",
    desc: "Submit posts to subreddits in your authentic voice",
    permissions: ["Read your account info", "Submit posts on your behalf"],
    copyInstructions: "Go to the subreddit you want to post in → click 'Create Post' → paste your content → Submit.",
    comingSoon: true,
  },
  {
    id: "instagram" as const,
    name: "Instagram",
    bg: "bg-pink-500/10",
    border: "border-pink-500/20",
    glow: "shadow-pink-500/10",
    desc: "Create captions for your Instagram posts",
    permissions: ["Read your business profile", "Create media on your behalf"],
    copyInstructions: "Open the Instagram app → tap + → choose your photo → paste your generated caption in the caption field → Share.",
    comingSoon: true,
  },
  {
    id: "facebook" as const,
    name: "Facebook",
    bg: "bg-indigo-500/10",
    border: "border-indigo-500/20",
    glow: "shadow-indigo-500/10",
    desc: "Post personal updates and stories to Facebook",
    permissions: ["Read your public profile", "Create posts on your behalf"],
    copyInstructions: "Go to facebook.com → click 'What\u2019s on your mind?' → paste your generated post → click Post.",
    comingSoon: true,
  },
];

function PlatformCard({ platform }: { platform: typeof PLATFORMS[0] }) {
  const [showCopy, setShowCopy] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopyInstructions = () => {
    navigator.clipboard.writeText(platform.copyInstructions);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Instructions copied!" });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-card border border-border rounded-2xl overflow-hidden shadow-lg ${platform.glow}`}
    >
      <div className="p-5 flex flex-col sm:flex-row sm:items-start gap-4">
        <div className={`w-12 h-12 rounded-xl ${platform.bg} border ${platform.border} flex items-center justify-center flex-shrink-0`}>
          <SocialIcon platform={platform.id} size={24} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-semibold text-foreground">{platform.name}</h3>
            {platform.comingSoon && (
              <span className="text-[10px] font-semibold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 rounded-full">
                Coming soon
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{platform.desc}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {platform.permissions.map((p) => (
              <span key={p} className="inline-flex items-center gap-1 text-xs bg-secondary text-muted-foreground px-2 py-0.5 rounded-md">
                <CheckCircle2 className="w-3 h-3 text-emerald-500" /> {p}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-row sm:flex-col gap-2 flex-shrink-0">
          <Button
            disabled
            size="sm"
            className="rounded-xl opacity-50 cursor-not-allowed gap-2"
            title="Direct posting coming soon — connect will be available in a future update"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Connect
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl gap-2"
            onClick={() => setShowCopy(!showCopy)}
          >
            <ClipboardCopy className="w-3.5 h-3.5" />
            Copy & Paste
            {showCopy ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {showCopy && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className={`mx-5 mb-5 p-4 rounded-xl ${platform.bg} border ${platform.border}`}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">How to post manually</p>
              <p className="text-sm text-foreground leading-relaxed">{platform.copyInstructions}</p>
              <button
                onClick={handleCopyInstructions}
                className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied!" : "Copy these instructions"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function IntegrationsContent() {
  return (
    <div className="space-y-8">
      <p className="text-muted-foreground">
        Connect your social accounts to post directly — or just copy and paste. Both work perfectly.
      </p>

      {/* Privacy first banner */}
      <div className="relative rounded-3xl overflow-hidden border border-emerald-500/20 bg-emerald-500/5 p-6">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 70% 50%, hsl(160 60% 50%), transparent 60%)" }} />
        <div className="relative flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="font-display font-bold text-lg text-foreground mb-1">Your privacy is the default, not an option</h2>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
              ScribPost never stores your passwords. Connecting an account uses OAuth — you authenticate on the platform's own website and we receive only a limited posting token.
              You can revoke this at any time. If you'd rather not connect anything, the copy-and-paste workflow is just as powerful.
            </p>
          </div>
        </div>
      </div>

      {/* Security breakdown */}
      <section className="space-y-4">
        <h2 className="text-xl font-display font-bold flex items-center gap-2">
          <Lock className="w-5 h-5 text-primary" /> How we keep you safe
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {SECURITY_POINTS.map((point) => (
            <div key={point.title} className="flex gap-3 p-4 bg-card border border-border rounded-2xl">
              <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                <point.icon className="w-4.5 h-4.5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{point.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{point.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Platform cards */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-display font-bold flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" /> Social Platforms
          </h2>
          <span className="text-xs text-muted-foreground bg-secondary px-2.5 py-1 rounded-full">
            Direct posting coming soon
          </span>
        </div>

        <div className="flex items-start gap-3 p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-muted-foreground">
            While we build native OAuth connections, use <span className="font-medium text-foreground">Copy & Paste</span> on any platform below — it works immediately, no setup needed.
            The generated content is identical whether you post directly or paste it yourself.
          </p>
        </div>

        <div className="space-y-3">
          {PLATFORMS.map((platform) => (
            <PlatformCard key={platform.id} platform={platform} />
          ))}
        </div>
      </section>

      {/* Bottom trust note */}
      <div className="flex items-start gap-3 p-5 bg-card border border-border rounded-2xl">
        <Info className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
        <div className="text-sm text-muted-foreground space-y-1">
          <p>
            <span className="font-medium text-foreground">Prefer full control?</span> You can always generate a post in ScribPost, copy it, and paste it directly into any platform.
            No account connection required — ever.
          </p>
          <p>
            Direct OAuth connections will allow one-click publishing from the Post Generator.
            When available, you'll see a "Post Now" button appear alongside your generated content.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Integrations() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto space-y-10"
    >
      <div>
        <h1 className="text-3xl font-display font-bold">Integrations</h1>
        <p className="text-muted-foreground mt-2">
          Connect your social accounts to post directly — or just copy and paste. Both work perfectly.
        </p>
      </div>

      <IntegrationsContent />
    </motion.div>
  );
}
